// src/pages/business/payments/PaymentsPage.tsx
// Mimics the mobile MpesaTransactionsScreen with enhanced web functionality

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  RefreshCw,
  CheckCircle,
  Clock,
  XCircle,
  HelpCircle,
  Receipt,
  Loader2,
  Download,
  Filter,
} from 'lucide-react';
import apiClient from '@/services/apiClient';
import { useToast } from '@/contexts/ToastContext';

interface Transaction {
  id: string;
  status: 'completed' | 'pending' | 'failed';
  amount?: string | number;
  created_at: string;
  phone_number?: string;
  result_description?: string;
  mpesa_receipt_number?: string;
}

interface Stats {
  total: number;
  completed: number;
  pending: number;
  failed: number;
  totalAmount: number;
}

const formatAmount = (amount?: string | number) => {
  const num = Number(amount || 0);
  return `KES ${num.toLocaleString()}`;
};

const formatPhone = (phone?: string) => {
  if (!phone) return 'N/A';
  return phone.replace('254', '0');
};

const formatTime = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

// ── Stats Card Component ──────────────────────────────────────────────────────
const StatsCard: React.FC<{
  title: string;
  value: React.ReactNode;
  subtitle?: string;
  color: string;
}> = ({ title, value, subtitle, color }) => {
  return (
    <div className="bg-light-card dark:bg-dark-card rounded-xl p-4 flex-1 min-w-[45%]">
      <p className="text-2xl font-bold" style={{ color }}>
        {value}
      </p>
      <p className="text-sm font-semibold text-light-text dark:text-dark-text mt-1">
        {title}
      </p>
      {subtitle && (
        <p className="text-xs text-light-subtle dark:text-dark-subtle mt-0.5">
          {subtitle}
        </p>
      )}
    </div>
  );
};

// ── Transaction Item Component ────────────────────────────────────────────────
const TransactionItem: React.FC<{ item: Transaction }> = ({ item }) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return '#10B981';
      case 'pending':
        return '#F59E0B';
      case 'failed':
        return '#EF4444';
      default:
        return '#6B7280';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle size={16} className="text-white" />;
      case 'pending':
        return <Clock size={16} className="text-white" />;
      case 'failed':
        return <XCircle size={16} className="text-white" />;
      default:
        return <HelpCircle size={16} className="text-white" />;
    }
  };

  return (
    <div className="bg-light-card dark:bg-dark-card rounded-xl p-4 flex items-center justify-between gap-4">
      {/* Left Section */}
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: getStatusColor(item.status) }}
        >
          {getStatusIcon(item.status)}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-light-text dark:text-dark-text truncate" style={{ fontSize: 15 }}>
            {formatPhone(item.phone_number)}
          </p>
          <p className="text-sm text-light-subtle dark:text-dark-subtle truncate">
            {item.result_description || 'Payment request sent'}
          </p>
          <p className="text-xs text-light-subtle dark:text-dark-subtle">
            {formatTime(item.created_at)}
          </p>
        </div>
      </div>

      {/* Right Section */}
      <div className="text-right flex-shrink-0">
        <p className="font-bold text-light-text dark:text-dark-text" style={{ fontSize: 15 }}>
          {formatAmount(item.amount)}
        </p>
        {item.mpesa_receipt_number && (
          <p className="text-xs text-light-subtle dark:text-dark-subtle">
            {item.mpesa_receipt_number}
          </p>
        )}
        <span
          className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold text-white mt-1"
          style={{ backgroundColor: getStatusColor(item.status) }}
        >
          {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
        </span>
      </div>
    </div>
  );
};

// ── Main Page Component ────────────────────────────────────────────────────────
const PaymentsPage: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();
  const { error: toastError } = useToast();

  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [stats, setStats] = useState<Stats>({
    total: 0,
    completed: 0,
    pending: 0,
    failed: 0,
    totalAmount: 0,
  });

  const loadTransactions = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get(`/business/${businessId}/mpesa/transactions?limit=100`);

      if (res.data.transactions) {
        const txs: Transaction[] = res.data.transactions;
        setTransactions(txs);

        // Calculate stats
        const completedCount = txs.filter((t) => t.status === 'completed').length;
        const pendingCount = txs.filter((t) => t.status === 'pending').length;
        const failedCount = txs.filter((t) => t.status === 'failed').length;
        const totalAmount = txs
          .filter((t) => t.status === 'completed')
          .reduce((sum, t) => sum + Number(t.amount || 0), 0);

        setStats({
          total: res.data.total || txs.length || 0,
          completed: completedCount,
          pending: pendingCount,
          failed: failedCount,
          totalAmount,
        });
      }
    } catch (error: any) {
      console.error('Failed to load transactions:', error);
      toastError('Failed to load transactions');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadTransactions();
  }, [businessId]);

  const onRefresh = async () => {
    setRefreshing(true);
    await loadTransactions();
  };

  // Group transactions by date
  const groupedTransactions = useMemo(() => {
    const grouped: Record<string, Transaction[]> = {};

    transactions.forEach((transaction) => {
      const date = formatDate(transaction.created_at);
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(transaction);
    });

    return Object.entries(grouped).map(([date, data]) => ({
      title: date,
      data: data,
    }));
  }, [transactions]);

  if (loading) {
    return (
      <div className="min-h-screen bg-light-bg dark:bg-dark-bg">
        <div className="sticky top-0 z-40 bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(`/business/${businessId}`)} className="flex-shrink-0">
            <ArrowLeft size={24} className="text-primary" />
          </button>
          <h1 className="flex-1 font-bold text-light-text dark:text-dark-text" style={{ fontSize: 20 }}>
            M-Pesa Transactions
          </h1>
        </div>
        <div className="flex items-center justify-center py-24">
          <div className="text-center">
            <Loader2 size={40} className="animate-spin mx-auto text-primary" />
            <p className="mt-4 text-light-subtle dark:text-dark-subtle">Loading transactions...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-light-bg dark:bg-dark-bg pb-24">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate(`/business/${businessId}`)} className="flex-shrink-0">
          <ArrowLeft size={24} className="text-primary" />
        </button>
        <h1 className="flex-1 font-bold text-light-text dark:text-dark-text" style={{ fontSize: 20 }}>
          M-Pesa Transactions
        </h1>
        <button
          onClick={onRefresh}
          disabled={refreshing}
          className="p-2"
        >
          <RefreshCw size={20} className={`text-primary ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="max-w-5xl mx-auto px-4 pt-4">
        {/* Stats Section */}
        <div className="mb-6">
          <p className="text-lg font-bold text-light-text dark:text-dark-text mb-4">Overview</p>
          <div className="grid grid-cols-2 gap-3">
            <StatsCard
              title="Total Transactions"
              value={stats.total}
              subtitle="All time"
              color="#4F46E5"
            />
            <StatsCard
              title="Completed"
              value={stats.completed}
              subtitle="Successful"
              color="#10B981"
            />
            <StatsCard
              title="Pending"
              value={stats.pending}
              subtitle="Waiting"
              color="#F59E0B"
            />
            <StatsCard
              title="Total Amount"
              value={`KES ${stats.totalAmount.toLocaleString()}`}
              subtitle="Received"
              color="#10B981"
            />
          </div>
        </div>

        {/* Transactions List */}
        {transactions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center gap-4">
            <Receipt size={64} className="text-light-subtle dark:text-dark-subtle" />
            <p className="font-semibold text-light-text dark:text-dark-text" style={{ fontSize: 18 }}>
              No Transactions Yet
            </p>
            <p className="text-sm text-light-subtle dark:text-dark-subtle max-w-xs">
              Your M-Pesa transactions will appear here once customers start making payments.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {groupedTransactions.map((group) => (
              <div key={group.title}>
                <p className="font-semibold text-light-text dark:text-dark-text mb-3" style={{ fontSize: 15 }}>
                  {group.title}
                </p>
                <div className="space-y-3">
                  {group.data.map((transaction) => (
                    <TransactionItem key={transaction.id} item={transaction} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentsPage;